// Globals
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getManagersList, getOrdersList, ordersSelector } from '../../feature';

// Models
import { Roles } from '@/models/admins.model';
import { IOrder, OrdersStatus } from '@/models/orders.model';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';
import { selectAdminProfile } from '@/modules/AdminProfile';

// Components
import { StyledPageWrapper, StyledPageTitle } from './OrdersMainContainerStyles';
import { OrdersList, OrdersSummary } from '../../components';

// Helpers
import { GetOrdersListDTO } from '../../dtos';
import { OrdersFiltersFields, TGetOrdersQuery } from '../../helpers';
import { toNumber } from '@/utils';
import { useGridQuerySync } from '@/hooks';

const OrdersMainContainer = () => {
  const { t } = useTranslation(['orders']);
  const dispatch = useAppDispatch();
  const { isLoading, ordersList, query } = useAppState(ordersSelector);
  const profile = useAppState(selectAdminProfile);
  const { canManage } = usePermissions();
  // Assigning / filtering by an order's manager is part of regular order processing (order.manage).
  const canManageOrderManager = canManage(Resource.ORDER);
  const defaultStatusAppliedRef = useRef(false);

  const handlePageChange = (page: number) => {
    const dto = new GetOrdersListDTO({ ...query, page });
    dispatch(getOrdersList(dto));
    dispatch(changeQuery(dto));
  };

  const restoreFromUrl = (restoredQuery: TGetOrdersQuery) => {
    const nextQuery =
      profile?.role === Roles.LOGISTICIAN && !restoredQuery[OrdersFiltersFields.STATUS]
        ? { ...restoredQuery, [OrdersFiltersFields.STATUS]: OrdersStatus.READY_FOR_TTN }
        : restoredQuery;

    dispatch(changeQuery(nextQuery));
    dispatch(getOrdersList(new GetOrdersListDTO({ ...nextQuery, page: nextQuery.page ?? 1 })));
    defaultStatusAppliedRef.current = true;
  };

  useGridQuerySync(query as TGetOrdersQuery, restoreFromUrl, {
    arrayKeys: [OrdersFiltersFields.STORE_IDS],
    numberKeys: [OrdersFiltersFields.PAGE, OrdersFiltersFields.LIMIT, OrdersFiltersFields.NUMBER],
  });

  useEffect(() => {
    return () => {
      dispatch(changeQuery({}));
      defaultStatusAppliedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (canManageOrderManager) {
      dispatch(getManagersList());
    }
  }, [canManageOrderManager]);

  useEffect(() => {
    if (
      profile?.role === Roles.LOGISTICIAN &&
      !defaultStatusAppliedRef.current &&
      !query?.[OrdersFiltersFields.STATUS]
    ) {
      const dto = new GetOrdersListDTO({
        ...query,
        [OrdersFiltersFields.STATUS]: OrdersStatus.READY_FOR_TTN,
        page: query?.[OrdersFiltersFields.PAGE] ?? 1,
      });
      dispatch(changeQuery(dto));
      dispatch(getOrdersList(dto));
      defaultStatusAppliedRef.current = true;
    }
  }, [profile?.role, query]);

  return (
    <StyledPageWrapper>
      <StyledPageTitle>{t('orders_main_title')}</StyledPageTitle>
      <OrdersSummary count={ordersList?.meta?.orders_total} totalSum={toNumber(ordersList?.meta?.revenue_total)} />
      <OrdersList
        ordersList={ordersList?.data as IOrder[]}
        isLoading={isLoading}
        currentPage={query?.[OrdersFiltersFields.PAGE] || 1}
        onPageChange={handlePageChange}
        total={ordersList?.meta?.total ?? 0}
      />
    </StyledPageWrapper>
  );
};

export default OrdersMainContainer;
